import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Layer} from '@dropbox/dig-components/layer';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, RewindLine, SettingsLine} from '@dropbox/dig-icons/assets';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';
import {useAtom, useAtomValue} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';

import {CustomizeWelcomeSpotlight} from './CustomizeWelcomeSpotlight';
import styles from './Dashboard.module.css';
import {logDashboardInteraction} from './dashboardAnalytics';
import {dashboardCustomizeHintDismissedAtom} from './dashboardCustomizeHint';
import {DASHBOARD_COLUMN_OPTIONS, DashboardColumns} from './dashboardLayout';
import {TileId} from './dashboardModel';
import {getAddableTileIds, getTileLabel, getTileMeta} from './tileCatalog';

/** Above CustomizeWelcomeSpotlight (14); DIG tooltips default to Layer z-index 11. */
const WELCOME_TOOLTIP_LAYER_Z = 15;

const GLYPH_FRAME = 18;
const COLUMN_BAR_HEIGHT = 12;

const columnGlyphMetrics = (columns: DashboardColumns): {barWidth: number; gap: number} => {
  if (columns === 2) {
    return {barWidth: 5, gap: 2};
  }
  if (columns === 3) {
    return {barWidth: 3, gap: 2};
  }
  return {barWidth: 2, gap: 1};
};

/** Vertical bars showing grid column density, matching tile size glyph framing. */
const ColumnGlyph = ({columns, label}: {columns: DashboardColumns; label: string}) => {
  const {barWidth, gap} = columnGlyphMetrics(columns);
  return (
    <Tooltip title={label}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{width: GLYPH_FRAME, height: GLYPH_FRAME, gap}}
      >
        {Array.from({length: columns}, (_, index) => (
          <span
            key={index}
            style={{
              width: barWidth,
              height: COLUMN_BAR_HEIGHT,
              backgroundColor: 'currentColor',
              borderRadius: 1,
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
};

export const DashboardCustomizeBar = ({
  currentTileIds,
  isCustomized,
  userId,
  columns,
  onDone,
  onReset,
  onAddTile,
  onSetColumns,
}: {
  currentTileIds: ReadonlySet<TileId>;
  isCustomized: boolean;
  userId?: string;
  columns: DashboardColumns;
  onDone: () => void;
  onReset: () => void;
  onAddTile: (tileId: TileId) => void;
  onSetColumns: (columns: DashboardColumns) => void;
}) => {
  const flags = useAtomValue(growthbookCacheAtom);
  const addableTiles = getAddableTileIds(userId, flags).filter((id) => !currentTileIds.has(id));

  return (
    <Box
      className={styles.customizeBar}
      padding="12"
      marginBottom="8"
      borderRadius="Medium"
      borderStyle="Solid"
      borderColor="Border Subtle"
      backgroundColor="Background Base"
      boxShadow="Raised"
    >
      <Stack gap="8">
        <Split alignY="top" gap="8">
          <Split.Item width="fill">
            <Box>
              <Text isBold>{t('dashboard_customize_title')}</Text>
            </Box>
            <Text size="small" color="subtle">
              {t('dashboard_customize_hint')}
            </Text>
          </Split.Item>
          <Split.Item>
            <Box display="flex" alignItems="center" style={{gap: 8}}>
              <SegmentedControl
                aria-label={t('dashboard_columns_aria').toString()}
                selectedValue={String(columns)}
                onSelection={(value) => {
                  const next = Number(value);
                  if (next === 2 || next === 3 || next === 4) {
                    onSetColumns(next);
                  }
                }}
              >
                {DASHBOARD_COLUMN_OPTIONS.map((option) => {
                  const label = t('dashboard_columns_option_aria', {count: option}).toString();
                  return (
                    <SegmentedControl.Option
                      key={option}
                      variant="icon"
                      value={String(option)}
                      aria-label={label}
                    >
                      <ColumnGlyph columns={option} label={label} />
                    </SegmentedControl.Option>
                  );
                })}
              </SegmentedControl>
            </Box>
          </Split.Item>
        </Split>

        <Split alignY="bottom">
          <Split.Item width="fill">
            {addableTiles.length > 0 && (
              <Box>
                <Text size="small" color="subtle" isBold>
                  {t('dashboard_add_tiles')}
                </Text>
                <Box
                  display="flex"
                  marginTop="8"
                  flexWrap="wrap"
                  overflowY="auto"
                  style={{gap: 8, maxHeight: 120}}
                >
                  {addableTiles.map((tileId) => {
                    const meta = getTileMeta(tileId);
                    return (
                      <Button
                        key={tileId}
                        variant="outline"
                        size="small"
                        onClick={() => onAddTile(tileId)}
                        withIconStart={
                          <UIIcon
                            src={meta.icon}
                            size="small"
                            className={atoms({color: 'Text Subtle'})}
                          />
                        }
                        withIconEnd={<UIIcon src={AddLine} size="small" />}
                      >
                        {getTileLabel(tileId)}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Split.Item>

          <Split.Item>
            <Split alignY="center" gap="8">
              {isCustomized && (
                <Split.Item>
                  <Button
                    size="small"
                    variant="outline"
                    onClick={onReset}
                    withIconStart={<UIIcon src={RewindLine} />}
                  >
                    {t('dashboard_reset_default')}
                  </Button>
                </Split.Item>
              )}

              <Split.Item>
                <Button size="small" variant="primary" onClick={onDone}>
                  {t('dashboard_done')}
                </Button>
              </Split.Item>
            </Split>
          </Split.Item>
        </Split>
      </Stack>
    </Box>
  );
};

export const DashboardCustomizeButton = ({
  userId,
  onToggle,
}: {
  userId?: string;
  onToggle: () => void;
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [dismissedStore, setDismissedStore] = useAtom(dashboardCustomizeHintDismissedAtom);
  const [hoverOpen, setHoverOpen] = useState(false);

  const showWelcome = Boolean(userId) && !dismissedStore[userId!];

  useEffect(() => {
    if (!showWelcome) {
      return;
    }
    logDashboardInteraction({action: 'welcome_tooltip_show'});
  }, [showWelcome]);

  const dismissWelcome = useCallback(
    (target: 'got_it' | 'scrim' | 'gear') => {
      if (!userId) {
        return;
      }
      logDashboardInteraction({action: 'welcome_tooltip_dismiss', target});
      setDismissedStore((prev) => ({...prev, [userId]: true}));
    },
    [userId, setDismissedStore]
  );

  const handleToggle = () => {
    if (showWelcome) {
      dismissWelcome('gear');
    }
    onToggle();
  };

  const customizeButton = (
    <span
      ref={triggerRef}
      style={{display: 'inline-flex'}}
      onMouseEnter={() => {
        if (!showWelcome) {
          setHoverOpen(true);
        }
      }}
      onMouseLeave={() => setHoverOpen(false)}
    >
      <IconButton
        variant="transparent"
        size="small"
        onClick={handleToggle}
        aria-label={t('dashboard_customize_title').toString()}
      >
        <UIIcon src={SettingsLine} />
      </IconButton>
    </span>
  );

  if (showWelcome) {
    return (
      <>
        {customizeButton}
        <CustomizeWelcomeSpotlight
          anchorRef={triggerRef}
          onDismiss={() => dismissWelcome('scrim')}
        />

        <Layer _elevateByZ={WELCOME_TOOLTIP_LAYER_Z}>
          <Tooltip.Control
            variant="rich"
            isPortaled={false}
            open
            triggerRef={triggerRef}
            placement="bottom-end"
            auto
            maxWidth={280}
          >
            <Stack gap="12" padding="4">
              <Title>{t('dashboard_customize_welcome_title')}</Title>
              <Box>
                <Text color="subtle">{t('dashboard_customize_welcome_body')}</Text>
              </Box>
              <Box display="flex" justifyContent="flex-end">
                <Button variant="outline" onClick={() => dismissWelcome('got_it')}>
                  {t('got_it')}
                </Button>
              </Box>
            </Stack>
          </Tooltip.Control>
        </Layer>
      </>
    );
  }

  return (
    <>
      <Tooltip.Control open={hoverOpen} triggerRef={triggerRef} placement="top" auto>
        {t('dashboard_customize_title')}
      </Tooltip.Control>
      {customizeButton}
    </>
  );
};
